import {ModalsStateModel} from '@store/modals/modals-state.model';
import {Action, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {GroupService} from '@services/group/group.service';
import {
  CleanDevicesFromGroupToEdit,
  CleanDevicesWithoutGroup,
  CleanEventToEdit,
  CleanGroups,
  GetDevicesFromGroupToEdit,
  GetDevicesWithoutGroup,
  GetGroups,
  SetDeviceToEventEdit,
  SetEventToEdit,
  SetGroupToEventEdit,
  SetMediaToEventEdit,
  SetPlaylistToEventEdit,
  SetTimeToEditEvent, UpdateEvent
} from '@store/modals/modals.actions';
import {Observable} from 'rxjs';
import {IGroup} from '@interfaces/group';
import {mergeMap, tap} from 'rxjs/operators';
import {IResponse} from '@interfaces/response';
import {DeviceService} from '@services/device/device.service';
import {IDevice} from '@interfaces/devices';
import {produce} from 'immer';
import {ScheduleService} from '@services/schedule/schedule.service';
import {ToggleGroupedDevicesLoader, ToggleUngroupedDevicesLoader} from '@store/loader';
import {FetchScheduledDevices, FetchScheduledGroups} from '@store/schedule';

@State<ModalsStateModel>({
  name: 'modalsState',
  defaults: {
    groups: null,
    devicesWithoutGroup: null,
    devicesFromGroupToEdit: null,
    eventToEdit: {
      event: null,
      activeEventDeviceGroupName: '',
      description: '',
      scheduleId: null,
    },
  }
})
@Injectable()
export class ModalsState {
  constructor(
    private groupService: GroupService,
    private deviceService: DeviceService,
    private scheduleService: ScheduleService,
    private store: Store
  ) {
  }

  @Action(GetGroups, {cancelUncompleted: true})
  getGroups(ctx: StateContext<ModalsStateModel>, {params}: GetGroups): Observable<IResponse<IGroup>> {
    return this.groupService.getGroups(params)
      .pipe(
        tap((response: IResponse<IGroup>) => ctx.setState({...ctx.getState(), groups: response}))
      );
  }

  @Action(CleanGroups)
  setSelectedGroup(ctx: StateContext<ModalsStateModel>): ModalsStateModel {
    return ctx.patchState({groups: null});
  }

  @Action(GetDevicesWithoutGroup, {cancelUncompleted: true})
  getDevicesWithoutGroup(ctx: StateContext<ModalsStateModel>, {params}: GetDevicesWithoutGroup): Observable<IResponse<IDevice>> {
    this.store.dispatch(new ToggleUngroupedDevicesLoader(true));
    return this.deviceService.getDevices({
      noGroup: 1,
      ...params
    }).pipe(
      tap((response) => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          devicesWithoutGroup: {...response, rows: response.rows}
        });
        this.store.dispatch(new ToggleUngroupedDevicesLoader(false));
      })
    );
  }

  @Action(CleanDevicesWithoutGroup)
  cleanDevicesWithoutGroup(ctx: StateContext<ModalsStateModel>): ModalsStateModel {
    return ctx.patchState({devicesWithoutGroup: null});
  }

  @Action(GetDevicesFromGroupToEdit, {cancelUncompleted: true})
  getDevicesFromGroupToEdit(ctx: StateContext<ModalsStateModel>, {params}: GetDevicesFromGroupToEdit): Observable<IResponse<IDevice>> {
    this.store.dispatch(new ToggleGroupedDevicesLoader(true));
    return this.groupService.getDevicesByGroupId(params)
      .pipe(
        tap((response) => {
          const state = ctx.getState();
          ctx.setState({
            ...state,
            devicesFromGroupToEdit: {...response, rows: response.rows}
          });
          this.store.dispatch(new ToggleGroupedDevicesLoader(false));
        })
      );
  }


  @Action(CleanDevicesFromGroupToEdit)
  cleanDevicesFromGroupToEdit(ctx: StateContext<ModalsStateModel>): ModalsStateModel {
    return ctx.patchState({devicesFromGroupToEdit: null});
  }

  @Action(SetEventToEdit)
  setEventToEdit(ctx: StateContext<ModalsStateModel>, {
    payload,
    deviceGroupName,
    scheduleId,
    description
  }: SetEventToEdit): ModalsStateModel {
    const state = produce(ctx.getState(), draft => {
      draft.eventToEdit.event = payload;
      draft.eventToEdit.activeEventDeviceGroupName = deviceGroupName;
      draft.eventToEdit.scheduleId = scheduleId;
      draft.eventToEdit.description = description;
    });

    return ctx.setState(state);
  }

  @Action(CleanEventToEdit)
  cleanEventToEdit(ctx: StateContext<ModalsStateModel>): ModalsStateModel {
    const state = produce(ctx.getState(), draft => {
      draft.eventToEdit.event = null;
      draft.eventToEdit.activeEventDeviceGroupName = '';
      draft.eventToEdit.scheduleId = null;
    });

    return ctx.setState(state);
  }

  @Action(SetDeviceToEventEdit)
  setDeviceToEventEdit(ctx: StateContext<ModalsStateModel>, {device, name}: SetDeviceToEventEdit): ModalsStateModel {
    const state = produce(ctx.getState(), draft => {
      if (!draft.eventToEdit.event) return;
      draft.eventToEdit.event.device = device;
      draft.eventToEdit.activeEventDeviceGroupName = name;
    });

    return ctx.setState(state);
  }

  @Action(SetGroupToEventEdit)
  setGroupToEventEdit(ctx: StateContext<ModalsStateModel>, {group, name}: SetGroupToEventEdit): ModalsStateModel {
    const state = produce(ctx.getState(), draft => {
      if (!draft.eventToEdit.event) return;
      draft.eventToEdit.event.group = group;
      draft.eventToEdit.activeEventDeviceGroupName = name;
    });

    return ctx.setState(state);
  }

  @Action(SetPlaylistToEventEdit)
  setPlaylistToEventEdit(ctx: StateContext<ModalsStateModel>, {playlist, description, name}: SetPlaylistToEventEdit): ModalsStateModel {
    const state = produce(ctx.getState(), draft => {
      if (!draft.eventToEdit.event) return;
      draft.eventToEdit.event.playlist = playlist;
      draft.eventToEdit.event.playlistName = name;
      draft.eventToEdit.event.medias = [];
      draft.eventToEdit.description = description;
    });

    return ctx.setState(state);
  }

  @Action(SetMediaToEventEdit)
  setMediaToEventEdit(ctx: StateContext<ModalsStateModel>, {media, playlistName}: SetMediaToEventEdit): ModalsStateModel {
    const state = produce(ctx.getState(), draft => {
      if (!draft.eventToEdit.event) return;
      draft.eventToEdit.event.medias = media;
      draft.eventToEdit.event.playlistName = playlistName;
      draft.eventToEdit.event.playlist = null;
    });

    return ctx.setState(state);
  }

  @Action(SetTimeToEditEvent)
  setTimeToEditEvent(ctx: StateContext<ModalsStateModel>, {payload}: SetTimeToEditEvent): void {
    const state = produce(ctx.getState(), draft => {
      if (!draft.eventToEdit.event) return;
      draft.eventToEdit.event.startLocal = payload.startLocal;
      draft.eventToEdit.event.endLocal = payload.endLocal;
      draft.eventToEdit.event.endLocalTimestamp = payload.endLocalTimeStamp;
      draft.eventToEdit.event.scheduleType = payload.scheduleType;
      draft.eventToEdit.event.repetition = payload.repetition;
    });

    ctx.setState(state);
  }

  @Action(UpdateEvent)
  updateEvent(ctx: StateContext<ModalsStateModel>): Observable<any> | void{
    const id = ctx.getState().eventToEdit.scheduleId;
    const event = produce(ctx.getState().eventToEdit.event, draft => {
      if (!draft) return;
      // in playlistId exist we don't need to update playlist name
      if (draft.playlist) {
        draft.playlistName = null;
      }
    });
    if (!event || !id) return;

    return this.scheduleService.updateEvent(event, id)
      .pipe(
        mergeMap(() => this.store.dispatch([new FetchScheduledDevices(), new FetchScheduledGroups()]))
      );
  }
}
